// Imports for external libraries go here.
import React, { FC, useId } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.

import { HotelCardProps } from './HotelCard.types';
import { StyledHotelCard } from './HotelCard.styles';
import { Link, Image } from '@marriott/mi-ui-library';
import { MLogoContainer } from '@marriott/mi-web-ui-kit';
import { IMAGE_URL_DOMAIN } from '../../modules/constants/constant';
import { getCtaUrl } from '../../modules/utils/submitSearchRequestHelper';

// Use named rather than default exports.
export const HotelCard: FC<HotelCardProps> = ({ props, variation, showReviews, ctaLabel, viewPort, reviewsLabel }) => {
  // Generate a unique ID for the component if needed, rather than using a fixed ID.
  const uniqueId = useId();

  const cardData = props?.node ? props?.node : props;

  // Get dynamic image URL based on prop structure
  const getImageUrl = () => {
    if (viewPort?.includes('Mobile') || variation === 'list-view') {
      return IMAGE_URL_DOMAIN + cardData?.media?.primaryImage?.edges[0]?.node?.imageUrls?.square || '';
    } else {
      return IMAGE_URL_DOMAIN + cardData?.media?.primaryImage?.edges[0]?.node?.imageUrls?.classicHorizontal || '';
    }
  };

  const getCardContent = () => {
    // Get hotel name and image details from cardData
    const hotelName = cardData?.basicInformation?.name;
    const imageUrl = getImageUrl();
    const hotelReviews = cardData?.reviews;

    if (variation === 'list-view') {
      return (
        <div className="d-flex flex-row list-mini-card-container pb-3">
          <Image
            title={hotelName}
            defaultImageURL={imageUrl}
            altText={hotelName}
            customClass="list-mini-card-image"
            loading={'eager'}
          />
          <div className="d-flex flex-column justify-content-between ml-3 list-mini-card-details">
            <div className="t-subtitle-m m-ellipsis-3lines">{hotelName}</div>
            {showReviews && hotelReviews?.stars?.count && hotelReviews?.numberOfReviews?.count && (
              <div className="review-container t-font-xs">
                <span className="icon-star-fill mr-1"></span>
                <span className="mr-1">{hotelReviews?.stars?.count}</span>
                <span>
                  <Link
                    linkHref={getCtaUrl(cardData, true)}
                    target={props?.openInaNewTab ? '_blank' : '_self'}
                    linkClassName="m-link custom_click_track"
                    id={`review-card-${uniqueId}`}
                    custom_click_track_value={`${hotelName} |Reviews |external`}
                  >
                    {`(${hotelReviews?.numberOfReviews?.count ? hotelReviews?.numberOfReviews?.count : 0} ${
                      reviewsLabel || 'Reviews'
                    })`}
                  </Link>
                </span>
              </div>
            )}
            <Link
              linkHref={getCtaUrl(cardData)}
              target={cardData?.openInaNewTab ? '_blank' : '_self'}
              linkClassName="m-link-tertiary-button custom_click_track"
              id={`mini-card-container-${uniqueId}`}
              custom_click_track_value={`${hotelName} |${ctaLabel} |external`}
            >
              {ctaLabel}
            </Link>
          </div>
        </div>
      );
    } else {
      if (viewPort?.includes('Mobile')) {
        return (
          <div className="d-flex flex-row mobile-mini-card-container p-2 color-scheme1">
            <Image
              title={hotelName}
              defaultImageURL={imageUrl}
              altText={hotelName}
              customClass="mobile-mini-card-image"
              loading={'eager'}
            />
            <div className="d-flex flex-column justify-content-between ml-2 mobile-mini-card-details pb-2">
              <div className="t-subtitle-m m-ellipsis-3lines">{hotelName}</div>
              {showReviews && hotelReviews?.stars?.count && hotelReviews?.numberOfReviews?.count && (
                <div className="review-container my-2">
                  <span className="icon-star-fill mr-1 t-font-xs"></span>
                  <span className="mr-1 m-label-xs">{hotelReviews?.stars?.count}</span>
                  <span>
                    <Link
                      linkHref={getCtaUrl(cardData, true)}
                      target={props?.openInaNewTab ? '_blank' : '_self'}
                      linkClassName="m-link custom_click_track"
                      id={`review-card-${uniqueId}`}
                      custom_click_track_value={`${hotelName} |Reviews |external`}
                    >
                      {`(${hotelReviews?.numberOfReviews?.count ? hotelReviews?.numberOfReviews?.count : 0} Reviews)`}
                    </Link>
                  </span>
                </div>
              )}
              <Link
                linkHref={getCtaUrl(cardData)}
                target={cardData?.openInaNewTab ? '_blank' : '_self'}
                linkClassName="m-link-tertiary-button custom_click_track"
                id={`mini-card-container-${uniqueId}`}
                custom_click_track_value={`${hotelName} |${ctaLabel} |external`}
              >
                {ctaLabel}
              </Link>
            </div>
          </div>
        );
      } else {
        return (
          <div className="d-flex flex-column mini-card-container p-2 color-scheme1">
            <div className="mini-card-img-container">
              <Image
                title={hotelName}
                defaultImageURL={imageUrl}
                altText={hotelName}
                customClass="mini-card-image"
                loading={'eager'}
              />
              <div className="brand-logo-container color-scheme2">
                <MLogoContainer
                  brand={cardData?.basicInformation?.brand?.id}
                  aspectRatio={'3:2'}
                  width={90}
                  height={60}
                  alignment={'Bottom Left'}
                  isLink={false}
                  color={'background-aware'}
                  ariaRole="img"
                />
              </div>
            </div>
            <div className="d-flex flex-column justify-content-between mini-card-details pb-2 pl-1">
              <div className="py-2 t-subtitle-m">{hotelName}</div>
              {showReviews && hotelReviews?.stars?.count && hotelReviews?.numberOfReviews?.count && (
                <div className="review-container my-2">
                  <span className="icon-star-fill mr-1 t-font-xs"></span>
                  <span className="mr-1 m-label-xs">{hotelReviews?.stars?.count}</span>
                  <span>
                    <Link
                      linkHref={getCtaUrl(cardData, true)}
                      target={props?.openInaNewTab ? '_blank' : '_self'}
                      linkClassName="m-link custom_click_track"
                      id={`review-card-${uniqueId}`}
                      custom_click_track_value={`${hotelName} |Reviews |external`}
                    >
                      {`(${hotelReviews?.numberOfReviews?.count ? hotelReviews?.numberOfReviews?.count : 0} Reviews)`}
                    </Link>
                  </span>
                </div>
              )}
              <Link
                linkHref={getCtaUrl(cardData)}
                target={cardData?.openInaNewTab ? '_blank' : '_self'}
                linkClassName="m-link-tertiary-button custom_click_track"
                id={`mini-card-container-${uniqueId}`}
                custom_click_track_value={`${hotelName} |${ctaLabel} |external`}
              >
                {ctaLabel}
              </Link>
            </div>
          </div>
        );
      }
    }
  };

  return (
    <StyledHotelCard data-testid="luxury-HotelCard" data-component-name="m-luxury-HotelCard" viewport={viewPort}>
      <div className="hotel-card-container">{getCardContent()}</div>
    </StyledHotelCard>
  );
};
